import Container from '@risk-smart/themed-cloudscape-components/container';
import Header from '@risk-smart/themed-cloudscape-components/header';
import SpaceBetween from '@risk-smart/themed-cloudscape-components/space-between';
import Button from '@risksmart-app/components/src/button';
import NotFoundPage from '@risksmart-app/components/src/error-pages/NotFoundPage';
import { useFileDownload } from '@risksmart-app/components/src/file/useFileDownload';
import { useGetGuidParam } from '@risksmart-app/components/src/routes/routes.utils';
import i18n from '@risksmart-app/i18n/src/i18n';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { ObjectToolbarConfig } from 'src/blocks/object-toolbar';
import { ObjectToolbar } from 'src/blocks/object-toolbar';
import { AttestButton } from 'src/components/attest-button/AttestButton';
import Loading from 'src/components/loading';
import { PolicyDocumentPreview } from 'src/components/policy-document-preview/PolicyDocumentPreview';
import Tokens from 'src/components/tokens';
import { PageLayout } from 'src/layouts';

import Link from '@/components/link';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { publicPolicyFileUrl } from '@/utils/urls';

import { usePublicDocument } from './config';
import styles from './style.module.scss';

const Page = () => {
  const fileId = useGetGuidParam('fileId');
  const documentId = useGetGuidParam('documentId');
  const { loading, error, data } = usePublicDocument(documentId, fileId);
  const downloadFile = useFileDownload();
  const { t } = useTranslation(['taxonomy']);
  const { t: tt } = useTranslation(['common'], { keyPrefix: 'policy' });
  const objectToolbarEnabled = useIsFeatureFlagEnabled('dynamic_forms');

  const handleFileDownload = async () => {
    if (!data || data.type !== 'file') {
      return;
    }
    await downloadFile(
      {
        fileId: data.documentFile.FileId,
        fileName: data.fileName,
      },
      true
    );
  };

  // Object-level toolbar pilot (CUS-177) — flag-gated. This public policy file
  // page has no register/create actions, so the toolbar is otherwise title-only.
  // The page's only actions live in the legacy `actions` block, which
  // `PageLayout` drops when `headerOverride` is set — so both must be mapped for
  // the flag-on path to stay functional:
  //   • Download → the toolbar's auto-labelled "Download" (export) group, wired
  //     to the same `handleFileDownload`, present only when the record is a file
  //     (mirrors the legacy conditional render).
  //   • Attest → the self-contained `<AttestButton>` (its own status query,
  //     disabled/label state and confirm modal) is rendered in the page body
  //     flag-on rather than lifted into the declarative toolbar, since it cannot
  //     be expressed as a plain onClick descriptor without duplicating its state.
  // Version is rendered verbatim as the parenthesised counter, mirroring the
  // legacy title suffix `${Title} (${Version})`. Flag-off, the legacy `actions`
  // block still renders both controls in the page header.
  const toolbarConfig: ObjectToolbarConfig = useMemo(
    () => ({
      header: {
        title: data?.documentFile.parent?.Title ?? '',
        counter: data ? `(${data.documentFile.Version})` : undefined,
      },
      ...(data?.type === 'file'
        ? {
            actions: {
              export: [
                {
                  id: 'download',
                  actions: [
                    {
                      id: 'download-file',
                      // Same key the page's own download buttons use
                      // (`PolicyDocumentPreview`), so the toolbar action reads
                      // identically to the legacy header button.
                      label: tt('publicFile.button'),
                      iconName: 'download-01',
                      onClick: () => {
                        void handleFileDownload();
                      },
                    },
                  ],
                },
              ],
            },
          }
        : {}),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, tt]
  );

  if (error) {
    return <NotFoundPage />;
  }

  const title = data
    ? `${data?.documentFile.parent?.Title} (${data?.documentFile.Version})`
    : '';

  const owners = [
    ...(data?.documentFile.parent?.owners ?? []),
    ...(data?.documentFile.parent?.ownerGroups ?? []),
  ];

  return (
    <PageLayout
      helpTranslationKey={'policy.publicPolicyFileHelp'}
      title={title}
      headerOverride={
        objectToolbarEnabled ? (
          <ObjectToolbar config={toolbarConfig} />
        ) : undefined
      }
      actions={
        <SpaceBetween direction={'horizontal'} size={'xs'}>
          {data?.type === 'file' ? (
            <Button onClick={handleFileDownload}>
              {'Download'}
              {/* TODO: translation*/}
            </Button>
          ) : null}
          {data?.documentFile ? (
            <AttestButton parentId={data.documentFile.Id} />
          ) : null}
        </SpaceBetween>
      }
    >
      {/* Flag-on, `headerOverride` (the ObjectToolbar) replaces the legacy
          header `actions`, so the Attest control is surfaced here in the body to
          keep it reachable. Flag-off it stays in the header `actions` above. */}
      {objectToolbarEnabled && data?.documentFile ? (
        <div className={'flex justify-end mb-6'}>
          <AttestButton parentId={data.documentFile.Id} />
        </div>
      ) : null}
      <div className={'flex lg:items-start flex-col-reverse lg:flex-row gap-6'}>
        <Container {...{ className: 'flex-1' }}>
          {!data || loading ? (
            <Loading />
          ) : (
            <PolicyDocumentPreview data={data} />
          )}
        </Container>
        <Container {...{ className: styles.sideBarWrapper }}>
          {!data || loading ? (
            <Loading />
          ) : (
            <div className={'flex flex-col gap-3'}>
              <Header variant={'h2'}>{tt('details')}</Header>
              <div>
                <div>
                  {tt('publicVersion')}
                  {':'} <strong>{data.documentFile.Version}</strong>
                </div>
                {data.documentFile.PublishedDate ? (
                  <div>
                    {tt('publicPublishedDate')}
                    {':'}{' '}
                    <strong>
                      {dayjs(data.documentFile.PublishedDate).format(
                        'DD MMM YYYY'
                      )}
                    </strong>
                  </div>
                ) : null}
                <div>
                  {i18n.format(
                    t('owner', { count: owners.length }),
                    'capitalizeAll'
                  )}
                  {':'}{' '}
                  <strong>
                    {owners
                      .map((o) =>
                        'user' in o
                          ? o.user?.FriendlyName
                          : 'group' in o
                            ? o.group?.Name
                            : ''
                      )
                      .join(', ')}
                  </strong>
                </div>
              </div>
              {(data.documentFile.parent?.tags.length ?? 0) > 0 ? (
                <div className={'mb-3'}>
                  {i18n.format(t('tag_one'), 'capitalizeAll')}
                  {':'}{' '}
                  <Tokens
                    tokens={
                      data.documentFile.parent?.tags.map((tag) => ({
                        value: tag.TagTypeId,
                        label: tag.type?.Name ?? '',
                      })) ?? []
                    }
                    disabled={true}
                    onRemove={() => null}
                  />
                </div>
              ) : null}
              {(data.documentFile.parent?.linkedDocuments.length ?? 0) > 0 ? (
                <div>
                  <div>
                    {tt('publicLinkedDocuments')}
                    {':'}
                  </div>
                  <ul className={'list-none p-0 m-0 mt-2'}>
                    {data.documentFile.parent?.linkedDocuments.map((doc, i) => (
                      <li key={i}>
                        <Link
                          key={i}
                          href={publicPolicyFileUrl(doc.child?.Id ?? '')}
                        >
                          <strong>{doc.child?.Title}</strong>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          )}
        </Container>
      </div>
    </PageLayout>
  );
};

export default Page;
